<script setup lang="ts">
import {FeatureHighlight} from "@/core/interfaces/feature-highlight/FeatureHighlight";
import {CalendarDays} from "@lucide/vue";
import {Switch} from "@/core/components/ui/switch";
import {nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import dayjs from "dayjs";
import {featureHighlightApi} from "@/container";
import {useRouter} from "vue-router";
import {Label} from "@/core/components/ui/label";
import {useToastStore} from "@/stores/toast.store";
import {refreshCurrentFeatureHighlight} from "@/home/composables/useCurrentFeatureHighlight";

const props = defineProps<{
    featureHighlight: FeatureHighlight;
    /** Whether the cached feature highlight is being refetched, its attachment may be outdated. */
    validating?: boolean;
}>();

const emailAlerts = ref<boolean>(props.featureHighlight.is_attached ?? false);
const loading = ref<boolean>(false);
const countdown = ref<{ days: number; time: string }>({days: 0, time: '00:00:00'});
const router = useRouter();
const {showError} = useToastStore();

let intervalId: NodeJS.Timeout | null = null;

/**
 * Follow the attachment returned by the server, the cached feature highlight
 * being revalidated after the component is mounted.
 */
watch(() => props.featureHighlight.is_attached, (isAttached) => {
    if (!loading.value) {
        emailAlerts.value = isAttached ?? false;
    }
});

/**
 * Toggle the email alert, then refetch the cached feature highlight and align
 * the switch on it, which also restores the previous value if the request fails.
 */
const toggleEmailAlerts = (value: boolean) => {
    if (loading.value) {
        return;
    }

    emailAlerts.value = value;
    loading.value = true;
    featureHighlightApi().toggle(props.featureHighlight.id)
        .catch(() => showError({content: "Une erreur est survenue lors de la mise à jour de l'alerte email."}))
        .then(() => refreshCurrentFeatureHighlight())
        // Let the refetched feature highlight reach the props before reading it.
        .then(() => nextTick())
        .catch(() => undefined)
        .finally(() => {
            loading.value = false;
            emailAlerts.value = props.featureHighlight.is_attached ?? false;
        });
};

/**
 * Open the webinar in the browser.
 */
const openWebinar = () => {
    window.ipcRenderer.openInBrowser(props.featureHighlight.webinar_url);
};

/**
 * Open the replay in the browser.
 */
const openReplay = () => {
    router.push({name: 'feature-highlights'});
};

/**
 * Update the countdown timer.
 */
const updateCountdown = () => {
    const now = dayjs();
    const end = dayjs(props.featureHighlight.end_at);
    const diff = end.diff(now, 'second');

    if (diff <= 0) {
        countdown.value = {days: 0, time: '00:00:00'};
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
        return;
    }

    const secondsInHour = 60 * 60;
    const secondsInDay = secondsInHour * 24;

    const days = Math.floor(diff / secondsInDay);
    const remaining = diff % secondsInDay;
    const hours = Math.floor(remaining / secondsInHour);
    const minutes = Math.floor((remaining % secondsInHour) / 60);
    const seconds = remaining % 60;

    countdown.value = {
        days,
        time: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`,
    };
};

onMounted(() => {
    updateCountdown();
    intervalId = setInterval(updateCountdown, 1000);
});

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
});
</script>

<template>
  <div class="bg-white rounded-lg p-4">
    <div class="flex flex-row justify-between">
      <div class="flex flex-col">
        <div class="flex flex-row items-center gap-2">
          <div class="bg-primary/15 py-1 px-3 rounded-full">
            <span class="text-primary">VENDREDI EXPERT 🚀</span>
          </div>
          <p class="text-primary text-sm">
            Chaque semaine, un sujet Apodis
          </p>
        </div>
        <div class="flex flex-col gap-4 pt-2">
          <h2 class="text-xl font-semibold">
            {{ featureHighlight.title }}
          </h2>
          <div class="flex flex-row items-center gap-4">
            <div class="flex flex-row items-center bg-gray-100 py-1 px-2 rounded-lg gap-2">
              <CalendarDays class="size-5 shrink-0 text-primary" />
              <span class="text-sm  pt-1">{{ featureHighlight.catchphrase }}</span>
            </div>
            <div class="flex flex-row items-center gap-2">
              <Switch :model-value="emailAlerts" :disabled="loading || validating" @update:model-value="toggleEmailAlerts" />
              <Label class="text-sm  pt-1">Être alerté par email 2h avant</Label>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col items-end justify-between">
        <div class="flex flex-col items-center gap-2">
          <div
            v-if="featureHighlight.end_at"
            class="relative flex w-56 flex-col items-center gap-1 bg-primary/10 border border-primary/20 rounded-lg px-6 pb-4 pt-2"
          >
            <div v-if="dayjs(featureHighlight.end_at).isAfter(dayjs())" class="flex flex-row items-baseline gap-1 pb-1">
              <template v-if="countdown.days > 0">
                <span class="text-xl font-bold text-primary tabular-nums tracking-tight">{{ countdown.days }}</span>
                <span class="font-bold text-primary">{{ countdown.days > 1 ? 'jours' : 'jour' }}</span>
                <span class="text-primary font-light">et</span>
              </template>
              <span class="text-xl font-bold text-primary tabular-nums tracking-tight">{{ countdown.time }}</span>
            </div>
            <div v-else class="flex flex-row items-baseline gap-1 pb-1">
              <span class="text-sm font-bold text-primary tabular-nums tracking-tight">Démo en cours !</span>
            </div>
            <div class="absolute -bottom-5">
              <BaseButton primary @click="openWebinar">
                Rejoindre la démo
              </BaseButton>
            </div>
          </div>
        </div>
        <div class="flex">
          <p class="text-sm text-primary hover:opacity-50 cursor-pointer" @click="openReplay">
            Voir les replays >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>
